import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type {
  DirectoryAgentType,
  DirectoryHumanType,
  GetPeopleResponseBody,
  StartPersonConversationBody,
  StartPersonConversationResponseBody,
} from "@app/types/api/people";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher, SWRConfiguration } from "swr";

export function buildPeopleUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/people`;
}

export function usePeople({
  owner,
  disabled,
  refreshInterval,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
  refreshInterval?: SWRConfiguration<GetPeopleResponseBody>["refreshInterval"];
}) {
  const { fetcher } = useFetcher();
  const peopleFetcher: Fetcher<GetPeopleResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildPeopleUrl(owner.sId),
    peopleFetcher,
    { disabled, refreshInterval }
  );

  return {
    humans: data?.humans ?? emptyArray<DirectoryHumanType>(),
    agents: data?.agents ?? emptyArray<DirectoryAgentType>(),
    isPeopleLoading: !disabled && !error && !data,
    isPeopleError: !!error,
    mutatePeople: mutate,
  };
}

async function post<T>({
  url,
  body,
  failureTitle,
  sendNotification,
}: {
  url: string;
  body: unknown;
  failureTitle: string;
  sendNotification: ReturnType<typeof useSendNotification>;
}): Promise<Result<T, Error>> {
  try {
    const res = await clientFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errorData = await getErrorFromResponse(res);
      sendNotification({
        type: "error",
        title: failureTitle,
        description: errorData.message,
      });
      return new Err(new Error(errorData.message));
    }

    return new Ok((await res.json()) as T);
  } catch (e) {
    const error = normalizeError(e);
    sendNotification({
      type: "error",
      title: failureTitle,
      description: error.message,
    });
    return new Err(error);
  }
}

export function usePeopleActions({ owner }: { owner: LightWorkspaceType }) {
  const sendNotification = useSendNotification();

  const startConversation = useCallback(
    async (target: StartPersonConversationBody) =>
      post<StartPersonConversationResponseBody>({
        url: `${buildPeopleUrl(owner.sId)}/conversations`,
        body: target,
        failureTitle: "Failed to start the conversation",
        sendNotification,
      }),
    [owner.sId, sendNotification]
  );

  const inviteToConversation = useCallback(
    async (conversationId: string, personSId: string) =>
      post<{ invitedName: string }>({
        url: `${buildPeopleUrl(owner.sId)}/conversations/${conversationId}/invite`,
        body: { personSId },
        failureTitle: "Failed to invite",
        sendNotification,
      }),
    [owner.sId, sendNotification]
  );

  return { startConversation, inviteToConversation };
}
